import {
	afterNextRender,
	afterRenderEffect,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	input,
	model,
	signal,
	viewChild
} from '@angular/core';
import { IconName } from '@icon/assets';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { guid } from '@tools';
import { SymbolMessage } from '../../../../../models/symbol-message.model';

/**
 * One message: its time, then the body clamped to two lines.
 *
 * The card itself is not a toggle. A body that fits in two lines has nothing to open, so
 * only the ones that overflow grow a «بیشتر» button — and that button, rather than the
 * whole card, is the hit target, which keeps the card's meaning the same everywhere.
 */
@Component({
	selector: 'app-symbol-message-card',
	imports: [UIIconComponent, UiButtonComponent],
	templateUrl: './symbol-message-card.component.html',
	host: {
		class: 'block w-full'
	}
})
export class SymbolMessageCardComponent {
	readonly message = input.required<SymbolMessage>();
	readonly expanded = model(false);

	protected readonly IconName = IconName;

	readonly bodyId = `symbol-message-body-${guid()}`;

	private readonly body = viewChild.required<ElementRef<HTMLParagraphElement>>('body');

	/** Whether the body has more text than the two lines the clamp shows. */
	readonly clamped = signal(false);

	readonly toggleLabel = computed(() => (this.expanded() ? 'بستن' : 'بیشتر'));

	constructor() {
		const destroyRef = inject(DestroyRef);

		// Re-measure when the body text changes. A ResizeObserver would miss this: two
		// different bodies both clamp to the same two-line box, so the element's own size
		// never moves even though one of them overflows and the other does not. This also
		// covers the first render, so the button is never missing until something resizes.
		afterRenderEffect(() => {
			this.message();
			this.measure();
		});

		afterNextRender(() => {
			// Whether two lines are enough is a function of the width, so rotation and
			// split-screen can add or remove the overflow without the text changing.
			const observer = new ResizeObserver(() => this.measure());
			observer.observe(this.body().nativeElement);
			destroyRef.onDestroy(() => observer.disconnect());
		});
	}

	toggle(): void {
		this.expanded.update(value => !value);
	}

	/**
	 * Measured only while collapsed: expanding removes the clamp, so the element then
	 * reports its full height and would claim the overflow had gone — taking the button
	 * away with it and leaving the message stuck open.
	 */
	private measure(): void {
		if (this.expanded()) {
			return;
		}

		const element = this.body().nativeElement;
		// A pixel of slack: sub-pixel line heights leave scrollHeight a hair over clientHeight
		// on text that fits perfectly.
		this.clamped.set(element.scrollHeight - element.clientHeight > 1);
	}
}
